import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useHash } from 'hooks/use-hash';
import { useSearch } from 'hooks/use-search';
import { FieldFilter } from '../tasks/filters';
import { defaultGroupedFilter } from '../tasks/utils';
import { ViewPresetId } from './scheduler-config/view-presets/constants';
import {
  ValidViewPresets,
  ValidViewPresetsHoursPerWeek,
} from './scheduler-config/view-presets/view-presets';
import {
  from_hash,
  from_search,
  getResourceManagementSearchTransformer,
  viewPresetTransformer,
} from './transformers';
import { ResourceManagmentPageParams } from './types';

interface Result {
  pageParams: ResourceManagmentPageParams;
  setPageParams: (nextParams: ResourceManagmentPageParams) => void;
  replacePageParams: (nextParams: ResourceManagmentPageParams) => void;
  resetPageParams: () => void;
  toUrlFormat: (nextParams: ResourceManagmentPageParams) => string;
  fromUrlFormat: (url: string) => ResourceManagmentPageParams;
}

export const usePageParam = (
  lookup: GroupFieldsMeta,
  defaultGroup: FilterGroupType,
  loading: boolean,
  isHoursPerWeek?: boolean,
): Result => {
  const { hash, search, ...restLocationParams } = useLocation();
  const { push, replace } = useHistory();
  const searchTransformer = useMemo(
    () => getResourceManagementSearchTransformer(lookup, defaultGroup),
    [defaultGroup, lookup],
  );
  const [searchParams, _, getNextSearch] = useSearch(searchTransformer);
  const [viewPreset, _ignore, getNextViewPreset] = useHash(
    viewPresetTransformer(
      isHoursPerWeek ? ValidViewPresetsHoursPerWeek : ValidViewPresets,
    ),
  );
  const [pageParams, setPageParamsState] =
    useState<ResourceManagmentPageParams>({
      ...searchParams,
      viewPreset,
    });

  const defaultPageParams: ResourceManagmentPageParams = useMemo(
    () => ({
      viewPreset: ViewPresetId.Week,
      filter: defaultGroupedFilter(defaultGroup),
    }),
    [defaultGroup],
  );

  const setPageParams = useCallback(
    (nextParams: ResourceManagmentPageParams) => {
      if (loading) {
        return;
      }

      const { viewPreset: nextViewPreset, ...searchValues } = nextParams;

      setPageParamsState(nextParams);
      push({
        hash: getNextViewPreset(nextViewPreset),
        search: getNextSearch(searchValues),
        ...restLocationParams,
      });
    },
    [getNextSearch, getNextViewPreset, push, restLocationParams, loading],
  );
  const replacePageParams = useCallback(
    (nextParams: ResourceManagmentPageParams) => {
      if (loading) {
        return;
      }

      const { viewPreset: nextViewPreset, ...searchValues } = nextParams;

      setPageParamsState(nextParams);
      replace({
        hash: getNextViewPreset(nextViewPreset),
        search: getNextSearch(searchValues),
        ...restLocationParams,
      });
    },
    [getNextSearch, getNextViewPreset, replace, restLocationParams, loading],
  );
  const resetPageParams = useCallback(() => {
    setPageParams(defaultPageParams);
  }, [defaultPageParams, setPageParams]);

  useEffect(() => {
    const { viewPreset: localViewPreset, ...searchValues } = pageParams;

    if (
      viewPreset !== localViewPreset ||
      !isEqual(searchParams, {
        ...searchValues,
        filter: {
          ...searchValues.filter,
          groupCriteria: searchValues.filter.groupCriteria.filter((x) =>
            FieldFilter.isValid(x.criteria),
          ),
        },
      })
    ) {
      if (
        isHoursPerWeek &&
        [ViewPresetId.Day, ViewPresetId.DayZoomed].includes(viewPreset)
      ) {
        setPageParamsState({
          ...searchParams,
          viewPreset: ViewPresetId.Week,
        });
      } else {
        setPageParamsState({
          ...searchParams,
          viewPreset,
        });
      }
    }
  }, [pageParams, searchParams, viewPreset, isHoursPerWeek]);

  const toUrlFormat = useCallback(
    ({
      viewPreset: newViewPreset,
      ...searchValues
    }: ResourceManagmentPageParams) =>
      `${getNextSearch(searchValues)}${getNextViewPreset(newViewPreset)}`,
    [getNextSearch, getNextViewPreset],
  );
  const fromUrlFormat = useCallback(
    (urlPart: string): ResourceManagmentPageParams => {
      const [searchPart, hashPart] = urlPart.split('#');

      return {
        ...from_search(lookup, defaultGroup)(searchPart),
        viewPreset: from_hash(ValidViewPresets)(hashPart),
      };
    },
    [defaultGroup, lookup],
  );

  return {
    pageParams,
    setPageParams,
    replacePageParams,
    resetPageParams,
    toUrlFormat,
    fromUrlFormat,
  };
};
